import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FiSave, FiUpload } from "react-icons/fi";
import api from "../../services/api";
import BonafideCertificate from "../../components/BonafideCertificate";

const DEFAULT_POSITIONS = {
    certificateDate: { label: "Certificate Date", top: "1.77in", left: "6.10in" },
    studentName: { label: "Student Full Name", top: "2.75in", left: "2.55in" },
    className: { label: "Class / Course", top: "3.42in", left: "3.66in" },
    sessionStart: { label: "Session Start Year", top: "4.13in", left: "0.86in" },
    sessionEnd: { label: "Session End Year", top: "4.13in", left: "1.41in" },
    dob: { label: "Date of Birth", top: "4.13in", left: "6.06in" },
    dobInWords: { label: "DOB in Words", top: "4.72in", left: "1.41in" },
};

const DEFAULT_CONFIG = {
    templateUrl: "/bonafide-template.jpg",
    paperWidth: "7.40in",
    paperHeight: "6.73in",
    fontFamily: "'Kalam', cursive",
    fontSize: "14px",
    fontWeight: "600",
    fontColor: "#1a1a1a",
    positions: DEFAULT_POSITIONS,
};

export default function BonafideTemplateSettings() {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(true);

    useEffect(() => {
        api.get("/colleges/my-bonafide-template")
            .then((res) => {
                const fetched = res.data?.data || res.data || {};
                const mergedPositions = { ...DEFAULT_POSITIONS };
                if (fetched.positions) {
                    Object.keys(DEFAULT_POSITIONS).forEach((k) => {
                        if (fetched.positions[k]) {
                            mergedPositions[k] = { ...DEFAULT_POSITIONS[k], ...fetched.positions[k] };
                        }
                    });
                }
                setConfig({
                    ...DEFAULT_CONFIG,
                    ...fetched,
                    positions: mergedPositions,
                });
            })
            .catch((err) => toast.error("Could not load template settings: " + err.message))
            .finally(() => setLoading(false));
    }, []);

    const handlePosChange = (field, key, val) => {
        setConfig((prev) => ({
            ...prev,
            positions: {
                ...prev.positions,
                [field]: {
                    ...prev.positions[field],
                    [key]: val,
                },
            },
        }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please upload an image file (JPG, PNG).");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setConfig((prev) => ({ ...prev, templateUrl: reader.result }));
            toast.success("Bonafide template image loaded into preview!");
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put("/colleges/my-bonafide-template", config);
            toast.success("Bonafide Certificate template & positions saved successfully!");
        } catch (err) {
            toast.error(err.message || "Failed to save settings");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading Bonafide template calibration…
            </div>
        );
    }

    const mockData = {
        srNo: "501",
        certificateDate: "15/07/2026",
        studentName: "Johnathan Alexander Doe",
        className: "B. Pharm 2nd Year",
        sessionStart: "2025",
        sessionEnd: "2026",
        dob: "15/08/2004",
        dobInWords: "Fifteenth August Two Thousand Four",
    };

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
                <div>
                    <h1 className="page-title">Bonafide Certificate Template & Calibration</h1>
                    <p className="page-subtitle">Configure pre-printed form measurements and coordinates for your college</p>
                </div>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    <FiSave size={14} /> {saving ? "Saving…" : "Save Template Settings"}
                </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: "1.5rem", alignItems: "start" }}>
                {/* Settings Panel */}
                <div className="card" style={{ maxHeight: "82vh", overflowY: "auto", padding: "1.1rem" }}>
                    <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: "0.75rem" }}>
                        1. Template Image & Paper
                    </h3>

                    <div className="form-group">
                        <label className="form-label">Upload Scanned Pre-printed Template</label>
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.5rem",
                                padding: "0.65rem 1rem",
                                border: "1px dashed var(--primary)",
                                background: "#f0f9ff",
                                borderRadius: "var(--radius)",
                                cursor: "pointer",
                                fontSize: 13,
                                fontWeight: 600,
                                color: "var(--primary)",
                            }}
                        >
                            <FiUpload size={15} />
                            Choose Template Image
                            <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: "none" }} />
                        </label>
                        <span className="form-hint">Scanned image of your college pre-printed physical Bonafide form.</span>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                        <div className="form-group">
                            <label className="form-label">Paper Width</label>
                            <input
                                className="form-control"
                                value={config?.paperWidth || ""}
                                onChange={(e) => setConfig({ ...config, paperWidth: e.target.value })}
                                placeholder="7.40in"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Paper Height</label>
                            <input
                                className="form-control"
                                value={config?.paperHeight || ""}
                                onChange={(e) => setConfig({ ...config, paperHeight: e.target.value })}
                                placeholder="6.73in"
                            />
                        </div>
                    </div>

                    <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", margin: "1.25rem 0 0.5rem" }}>
                        2. Coordinate Positions (Inches)
                    </h3>

                    <div style={{ display: "grid", gap: "0.6rem" }}>
                        {Object.entries(config.positions).map(([field, pos]) => {
                            const label = pos.label || field.replace(/([A-Z])/g, " $1");
                            return (
                                <div
                                    key={field}
                                    style={{
                                        padding: "0.6rem 0.75rem",
                                        border: "1px solid var(--border)",
                                        borderRadius: 6,
                                        background: "#fff",
                                    }}
                                >
                                    <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 4, textTransform: "capitalize" }}>
                                        {label}
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px" }}>
                                        <div>
                                            <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Top</span>
                                            <input
                                                className="form-control"
                                                style={{ fontSize: 12, padding: "4px 8px" }}
                                                value={pos.top || ""}
                                                onChange={(e) => handlePosChange(field, "top", e.target.value)}
                                                placeholder="0.00in"
                                            />
                                        </div>
                                        <div>
                                            <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Left</span>
                                            <input
                                                className="form-control"
                                                style={{ fontSize: 12, padding: "4px 8px" }}
                                                value={pos.left || ""}
                                                onChange={(e) => handlePosChange(field, "left", e.target.value)}
                                                placeholder="0.00in"
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Live Calibration View */}
                <div className="card" style={{ padding: "1.1rem", overflowX: "auto" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>Live Calibration Preview</span>
                        <label style={{ fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                            <input
                                type="checkbox"
                                checked={showPreview}
                                onChange={(e) => setShowPreview(e.target.checked)}
                            />
                            Show Template Background
                        </label>
                    </div>

                    <div
                        style={{
                            border: "1px dashed #94a3b8",
                            display: "inline-block",
                            background: "#f8fafc",
                            padding: "4px",
                        }}
                    >
                        <BonafideCertificate
                            data={mockData}
                            printMode="printed"
                            previewMode={showPreview}
                            templateConfig={config}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}